import React, { useCallback } from "react";
import { object, array } from "prop-types";
import { WithLocale } from "@insightfulscience/i18";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import { LocaleProvider } from "@insightfulscience/smart-react/i18";

import SequencesPageTemplate from "@src/Plasmids/templates";
import sgApiConnection from "@src/Plasmids/services/sgApiConnection";
import sgApiClient from "@src/Plasmids/services/sgApiClient";
import SectionTryForFree from "@src/SectionTryForFree";
import SiteLayout from "@src/SiteLayout";
import { publicConfig } from "@src/config";
import WithClientSideServices from "@src/WithClientSideServices";
import { encodeURLFragment } from "@src/Plasmids/utils/encodeURLFragment";
import resources from "../../../locales/plasmids-sequences-page";

const PlasmidsSequencesPage = ({ sets, setSequencesData }) => {
	const siteDomain = publicConfig.STATIC_SITE_DOMAIN;

	const { setData, sequences } = setSequencesData;

	const title = setData.plasmidSetName;
	const description = setData.plasmidSetDesc;
	const setID = setData.set;
	const filename = setData.plasmidSetFilename;

	const downloadFile = useCallback(() => {
		const fileAnchor = document.createElement("a");
		document.body.appendChild(fileAnchor);
		fileAnchor.href = `https://${siteDomain}/api/plasmids/download?setID=${setID}`;
		fileAnchor.click();
		document.body.removeChild(fileAnchor);
	}, [siteDomain, setID]);

	return (
		<SiteLayout hero>
			<LocaleProvider resources={resources}>
				<SequencesPageTemplate
					typePage={setID}
					title={title}
					description={description}
					setID={setID}
					filename={filename}
					downloadFile={downloadFile}
					sets={sets}
					sequences={sequences}
				/>
			</LocaleProvider>
			<SectionTryForFree />
		</SiteLayout>
	);
};

PlasmidsSequencesPage.propTypes = {
	sets: array.isRequired,
	setSequencesData: object.isRequired,
};

export const getStaticPaths = async () => ({
	paths: [],
	fallback: "blocking",
});

export const getStaticProps = async ({ params: { setID } }) => {
	const apiConn = sgApiConnection(global.fetch, publicConfig.SG_API_BASE_URL);
	const apiClient = sgApiClient(apiConn);

	const sets = await apiClient.getSets();
	const setSequencesData = await apiClient.getSequences(
		encodeURLFragment(setID),
	);

	return sets && setSequencesData
		? {
				props: { sets, setSequencesData },
				revalidate: false,
			}
		: { notFound: true };
};

export default pipe(
	PlasmidsSequencesPage,
	WithLocale({ resources }),
	WithClientSideServices({
		sgApiConn: () =>
			sgApiConnection(global.fetch, publicConfig.SG_API_BASE_URL),
		sgApiClient: ({ sgApiConn }) => sgApiClient(sgApiConn),
	}),
);
